import { useNavigate, useLocation } from "react-router-dom";
import "./dashboardpage.css";
import {
  Speedometer,
  BodyText,
  Newspaper,
  PersonAdd,
  BoxArrowRight,
  FilterCircle,
  StickiesFill,
  EmojiSmile,
  Envelope,
  ClockHistory,
  ImageFill,
  FlagFill,
  PatchCheck,
  MegaphoneFill,
  Robot,
} from "react-bootstrap-icons";
import { useState, useEffect } from "react";
import Blog from "../layout/Dashboard/Blog/Blog";
import Newsletter from "../layout/Dashboard/Newsletter/Newsletter";
import Admin from "../layout/Dashboard/Admin/Admin";
import Assets from "../layout/Dashboard/Assets/Assets";
import Inquiries from "../layout/Dashboard/Inquiries/Inquiries";
import Reports from "../layout/Dashboard/Reports/Reports";
import Verification from "../layout/Dashboard/Verification/Verification";
import Broadcast from "../layout/Dashboard/Broadcast/Broadcast";
import Bots from "../layout/Dashboard/Bots/Bots";
import { useAuth } from "../hooks/AuthProvider";
import axios from "axios";
import API_BASE_URL from "../config/api";

const sections = [
  { name: "Overview", url: "/admin/dashboard", icon: <Speedometer className="sidebarIcon" /> },
  { name: "Blog", url: "/admin/dashboard/blog", icon: <BodyText className="sidebarIcon" /> },
  { name: "Newsletter", url: "/admin/dashboard/newsletter", icon: <Newspaper className="sidebarIcon" /> },
  { name: "Assets", url: "/admin/dashboard/assets", icon: <ImageFill className="sidebarIcon" /> },
  { name: "Inquiries", url: "/admin/dashboard/inquiries", icon: <Envelope className="sidebarIcon" /> },
  { name: "Reports", url: "/admin/dashboard/reports", icon: <FlagFill className="sidebarIcon" /> },
  { name: "Verification", url: "/admin/dashboard/verification", icon: <PatchCheck className="sidebarIcon" /> },
  { name: "Broadcast", url: "/admin/dashboard/broadcast", icon: <MegaphoneFill className="sidebarIcon" /> },
  { name: "Bots", url: "/admin/dashboard/bots", icon: <Robot className="sidebarIcon" /> },
  { name: "Admins", url: "/admin/dashboard/admins", icon: <PersonAdd className="sidebarIcon" /> },
];

function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const role = user?.role || null;
  const isAdmin = role === "ADMIN";
  const isSupport = role === "SUPPORT" || isAdmin;
  const isCreator = role === "CREATOR" || isAdmin;

  const allowedSections = sections.filter((s) => {
    if (s.url === "/admin/dashboard/admins") return isAdmin;
    if (s.url === "/admin/dashboard/assets") return isCreator; // CREATOR or ADMIN
    if (s.url === "/admin/dashboard/blog" || s.url === "/admin/dashboard/newsletter" ||
        s.url === "/admin/dashboard/inquiries" || s.url === "/admin/dashboard/reports" ||
        s.url === "/admin/dashboard/verification" || s.url === "/admin/dashboard/broadcast" ||
        s.url === "/admin/dashboard/bots") {
      return isSupport; // SUPPORT or ADMIN
    }
    return true; // Overview
  });

  const currentSection = allowedSections.find((s) => s.url === location.pathname) || allowedSections[0] || sections[0];
  const sectionAllowed = allowedSections.some((s) => s.url === location.pathname);

  return (
    <div className="dashboardCover">
      <aside className="dashboardSidebar">
        <div className="sidebarBrand">SoloChat</div>
        <ul>
          {allowedSections.map((item) => (
            <li
              key={item.url}
              className={location.pathname === item.url ? "active" : ""}
              onClick={() => navigate(item.url)}
            >
              {item.icon}
              <span>{item.name}</span>
            </li>
          ))}
        </ul>
        <div className="sidebarLogout" onClick={logout}>
          <BoxArrowRight className="sidebarIcon" />
          <span>Logout</span>
        </div>
      </aside>

      <main className="dashboardMain">
        <div className="dashboardHeader">
          <h2>{currentSection.name}</h2>
          <div className="userBadge">
            <div className="avatar">{(user?.name || user?.email || "A")[0].toUpperCase()}</div>
            <span>{user?.name || user?.email || "Admin"}</span>
            {role && <span className="userRoleBadge">{role}</span>}
          </div>
        </div>

        {sectionAllowed ? (
          <>
            {location.pathname === "/admin/dashboard" && <Overview />}
            {location.pathname === "/admin/dashboard/blog" && <Blog />}
            {location.pathname === "/admin/dashboard/newsletter" && <Newsletter />}
            {location.pathname === "/admin/dashboard/assets" && <Assets />}
            {location.pathname === "/admin/dashboard/inquiries" && <Inquiries />}
            {location.pathname === "/admin/dashboard/reports" && <Reports />}
            {location.pathname === "/admin/dashboard/verification" && <Verification />}
            {location.pathname === "/admin/dashboard/broadcast" && <Broadcast />}
            {location.pathname === "/admin/dashboard/bots" && <Bots />}
            {location.pathname === "/admin/dashboard/admins" && <Admin />}
          </>
        ) : (
          <div className="dashboardAccessDenied">
            <h3>Access denied</h3>
            <p>You don't have permission to view this section.</p>
            <button onClick={() => navigate("/admin/dashboard")}>Go to Overview</button>
          </div>
        )}
      </main>
    </div>
  );
}

function Overview() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const [stats, setStats] = useState({
    blogs: "-", subscribers: "-", users: "-",
    stickers: "-", filters: "-", memoji: "-", contacts: "-", wallpapers: "-", reports: "-",
    verifications: "-", bots: "-",
  });
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
        const [blogRes, subRes, userRes, stickerRes, filterRes, memojiRes, contactRes, wallpaperRes, reportRes, verifRes, botRes] =
          await Promise.allSettled([
            axios.get(`${API_BASE_URL}/api/v1/blog`),
            axios.get(`${API_BASE_URL}/api/v1/newsletter`),
            axios.get(`${API_BASE_URL}/api/v1/users`, authHeaders),
            axios.get(`${API_BASE_URL}/api/v1/sticker`),
            axios.get(`${API_BASE_URL}/api/v1/filter`),
            axios.get(`${API_BASE_URL}/api/v1/memoji`),
            axios.get(`${API_BASE_URL}/api/v1/contact`, authHeaders),
            axios.get(`${API_BASE_URL}/api/v1/wallpaper`),
            axios.get(`${API_BASE_URL}/api/v1/reports`, authHeaders),
            axios.get(`${API_BASE_URL}/api/v1/verification`, authHeaders),
            axios.get(`${API_BASE_URL}/api/v1/bot`, authHeaders),
          ]);

        const blogs = blogRes.status === "fulfilled" ? blogRes.value.data : [];
        const contacts = contactRes.status === "fulfilled" ? contactRes.value.data : [];
        const verifications = verifRes.status === "fulfilled" && Array.isArray(verifRes.value.data?.items)
          ? verifRes.value.data.items.filter((u) => u.verificationStatus === "pending").length
          : "-";

        setStats({
          blogs: Array.isArray(blogs) ? blogs.length : "-",
          subscribers: subRes.status === "fulfilled" && Array.isArray(subRes.value.data) ? subRes.value.data.length : "-",
          users: userRes.status === "fulfilled" && Array.isArray(userRes.value.data) ? userRes.value.data.length : "-",
          stickers: stickerRes.status === "fulfilled" && Array.isArray(stickerRes.value.data) ? stickerRes.value.data.length : "-",
          filters: filterRes.status === "fulfilled" && Array.isArray(filterRes.value.data) ? filterRes.value.data.length : "-",
          memoji: memojiRes.status === "fulfilled" && Array.isArray(memojiRes.value.data) ? memojiRes.value.data.length : "-",
          contacts: Array.isArray(contacts) ? contacts.length : "-",
          wallpapers: wallpaperRes.status === "fulfilled" && Array.isArray(wallpaperRes.value.data) ? wallpaperRes.value.data.length : "-",
          reports: reportRes.status === "fulfilled" ? (reportRes.value.data?.total ?? "-") : "-",
          verifications,
          bots: botRes.status === "fulfilled" && Array.isArray(botRes.value.data) ? botRes.value.data.length : "-",
        });

        if (Array.isArray(blogs)) setRecentBlogs(blogs.slice(0, 5));
        if (Array.isArray(contacts)) setRecentContacts(contacts.filter((c) => !c.seen).slice(0, 5));
      } catch {}
    })();
  }, [token]);

  return (
    <>
      <div className="welcomeCard">
        <h3>Welcome back{user?.name ? ", " + user.name : ""}</h3>
        <p>Here's what's happening with your platform today.</p>
      </div>

      <div className="statsGrid">
        <Stat icon={<BodyText />} color="#cf4633" bg="#fef0ef" value={stats.blogs} label="Blog Posts" />
        <Stat icon={<Newspaper />} color="#3b82f6" bg="#eef6ff" value={stats.subscribers} label="Subscribers" />
        <Stat icon={<StickiesFill />} color="#8b5cf6" bg="#f3eefe" value={stats.stickers} label="Stickers" />
        <Stat icon={<FilterCircle />} color="#06b6d4" bg="#e6fafe" value={stats.filters} label="Filters" />
        <Stat icon={<EmojiSmile />} color="#f59e0b" bg="#fef7e6" value={stats.memoji} label="Memoji" />
        <Stat icon={<ImageFill />} color="#6366f1" bg="#eef2ff" value={stats.wallpapers} label="Wallpapers" />
        <Stat icon={<Envelope />} color="#10b981" bg="#edfcf5" value={stats.contacts} label="Inquiries" />
        <Stat icon={<FlagFill />} color="#e11d48" bg="#ffe4ec" value={stats.reports} label="Reports" />
        <Stat icon={<Robot />} color="#8b5cf6" bg="#f3eefe" value={stats.bots} label="AI Bots" />
        <Stat icon={<PatchCheck />} color="#d66333" bg="#fdeee7" value={stats.verifications} label="Pending Verifications" />
        {isAdmin && <Stat icon={<PersonAdd />} color="#ec4899" bg="#fce8f3" value={stats.users} label="Admins" />}
      </div>

      <div className="overviewGrid">
        <div className="overviewCard">
          <div className="overviewCardHeader">
            <ClockHistory />
            <span>Recent Blog Posts</span>
          </div>
          {recentBlogs.length === 0 && <p className="text-muted p-3 m-0">No blog posts yet.</p>}
          {recentBlogs.map((post) => (
            <div key={post.id} className="overviewRow">
              <div className="overviewRowContent">
                <strong>{post.title}</strong>
                <small>{post.author ? `by ${post.author}` : ""} &middot; {new Date(post.created_at).toLocaleDateString()}</small>
              </div>
            </div>
          ))}
        </div>

        <div className="overviewCard">
          <div className="overviewCardHeader">
            <Envelope />
            <span>Unread Inquiries</span>
          </div>
          {recentContacts.length === 0 && <p className="text-muted p-3 m-0">No unread inquiries.</p>}
          {recentContacts.map((c) => (
            <div key={c.id} className="overviewRow">
              <div className="overviewRowContent">
                <strong>{c.email}</strong>
                <small>{c.body?.substring(0, 80)}{c.body?.length > 80 ? "..." : ""}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Stat({ icon, color, bg, value, label }) {
  return (
    <div className="statCard">
      <div className="statIcon" style={{ background: bg, color }}>
        {icon}
      </div>
      <div className="statInfo">
        <h4>{value}</h4>
        <p>{label}</p>
      </div>
    </div>
  );
}

export default DashboardPage;
